import { useToast } from '@chakra-ui/react';
import { QueryKey, useQuery } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { useAxios } from 'context/AxiosContextProvider';
import { manageErrorResponse } from 'helpers/manageErrorResponse';
import { useEffect } from 'react';

import { PaginatedServiceRecords } from '../api/types';
import config from '../config';

async function getServiceRecords(props: {
  axios: AxiosInstance;
  searchString?: string;
  page?: number;
  itemsPerPage?: number;
  status?: Array<string>;
  make?: string;
  language?: string;
  assignee?: string;
  fastlane?: number;
  workshopId?: number;
  systemPartner?: string;
  country?: string;
}): Promise<PaginatedServiceRecords> {
  const { axios, searchString, page, itemsPerPage, status = [], make, language, assignee, fastlane, workshopId, systemPartner, country } = props;

  const optimizedStatus = status.length === 9 ? "" : status.length > 0 ? status.join() : undefined;

  const { data } = await axios.get(`${config.apiBaseUrl}/op/v2/service-record`, {
    params: {
      searchString: searchString || '',
      page,
      itemsPerPage,
      status: optimizedStatus,
      make_id: make,
      language,
      workshop_id: workshopId,
      user_id: assignee,
      fastlane,
      systempartner: systemPartner,
      country,
    },
  });
  return data;
}

export default function useServiceRecordsQuery({
  searchString,
  page,
  itemsPerPage,
  status,
  make,
  language,
  assignee,
  fastlane,
  workshopId,
  systemPartner,
  country,
}: {
  searchString?: string;
  page?: number;
  itemsPerPage?: number;
  status?: Array<string>;
  make?: string;
  language?: string;
  assignee?: string;
  fastlane?: number;
  workshopId?: number;
  systemPartner?: string;
  country?: string;
}) {
  const axios = useAxios();
  const toast = useToast();

  const query = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: getKey({
      searchString,
      page,
      itemsPerPage,
      status,
      make,
      language,
      assignee,
      fastlane,
      workshopId,
      systemPartner,
      country,
    }),
    queryFn: () =>
      getServiceRecords({
        axios,
        searchString,
        page,
        itemsPerPage,
        status,
        make,
        language,
        assignee,
        fastlane,
        workshopId,
        systemPartner,
        country,
      }),
    notifyOnChangeProps: ['data', 'isFetching', 'error'],
    retry: false,
    refetchOnMount: 'always',
  });

  useEffect(() => {
    if (query.isError) {
      manageErrorResponse(toast, query.error as any);
    }
  }, [query.error, query.isError, toast]);

  return query;
}

export function getKey({
  searchString,
  page,
  itemsPerPage,
  status,
  make,
  language,
  assignee,
  fastlane,
  workshopId,
  systemPartner,
  country,
}: {
  searchString?: string;
  page?: number;
  itemsPerPage?: number;
  status?: Array<string>;
  make?: string;
  language?: string;
  assignee?: string;
  fastlane?: number;
  workshopId?: number;
  systemPartner?: string;
  country?: string;
}) {
  return [
    'ServiceRecords',
    searchString,
    page,
    itemsPerPage,
    status,
    make,
    language,
    assignee,
    fastlane,
    workshopId,
    systemPartner,
    country,
  ] as const satisfies QueryKey;
}

useServiceRecordsQuery.getKey = getKey;
